import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Link2, RefreshCw, Loader2, ExternalLink, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAiQuota } from '@/lib/useAiQuota';
import { AiQuotaInline } from '@/components/AiQuotaInline';

const TYPE_LABELS = { annuaire: 'Annuaire', blog: 'Blog', forum: 'Forum', partenaire: 'Partenaire', media: 'Media' };
const TYPE_LABELS_EN = { annuaire: 'Directory', blog: 'Blog', forum: 'Forum', partenaire: 'Partner', media: 'Media' };
const TYPE_COLORS = { annuaire: 'bg-blue-500/10 text-blue-600', blog: 'bg-purple-500/10 text-purple-600', forum: 'bg-amber-500/10 text-amber-600', partenaire: 'bg-green-500/10 text-green-600', media: 'bg-rose-500/10 text-rose-600' };
const PRIORITY_COLORS = { high: 'bg-red-500/10 text-red-600', medium: 'bg-amber-500/10 text-amber-600', low: 'bg-green-500/10 text-green-600' };
const STATUS_COLORS = { pending: 'bg-slate-500/10 text-slate-600', contacted: 'bg-blue-500/10 text-blue-600', obtained: 'bg-green-500/10 text-green-600', rejected: 'bg-red-500/10 text-red-600' };
const STATUS_LABELS = { pending: 'En attente', contacted: 'Contacte', obtained: 'Obtenu', rejected: 'Refuse' };
const STATUS_LABELS_EN = { pending: 'Pending', contacted: 'Contacted', obtained: 'Obtained', rejected: 'Rejected' };

export function BacklinksTab() {
  const { user } = useAuth();
  const { language: lang } = useLanguage();
  const isAdmin = ['admin', 'admin_enterprise'].includes(user?.role);
  const [backlinks, setBacklinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const { quota, refresh: refreshQuota, isReached } = useAiQuota();

  const t = (fr, en) => lang === 'fr' ? fr : en;

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const { data } = await api.get('/seo/backlinks');
      setBacklinks(data.backlinks);
    } catch { toast.error(t('Erreur', 'Error')); }
    finally { setLoading(false); }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post('/seo/backlinks/generate');
      setBacklinks(data.backlinks);
      toast.success(t(`${data.total} opportunites de backlinks generees`, `${data.total} backlink opportunities generated`));
      refreshQuota();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d && typeof d === 'object' && d.error === 'ai_quota_reached') {
        toast.error(t('Limite IA atteinte pour ce mois. Passez au forfait supérieur ou contactez l\'administrateur.', 'AI limit reached for this month. Upgrade your plan or contact your administrator.'));
        refreshQuota();
      } else {
        toast.error((typeof d === 'string' ? d : null) || t('Erreur', 'Error'));
      }
    } finally { setGenerating(false); }
  };

  const updateStatus = async (id, status) => {
    try {
      const { data } = await api.put(`/seo/backlinks/${id}`, { status });
      setBacklinks(prev => prev.map(b => b.backlink_id === id ? data : b));
    } catch { toast.error(t('Erreur', 'Error')); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/seo/backlinks/${id}`);
      setBacklinks(prev => prev.filter(b => b.backlink_id !== id));
      toast.success(t('Supprime', 'Deleted'));
    } catch { toast.error(t('Erreur', 'Error')); }
  };

  if (loading && backlinks.length === 0) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const stats = {
    total: backlinks.length,
    obtained: backlinks.filter(b => b.status === 'obtained').length,
    pending: backlinks.filter(b => b.status === 'pending').length,
    high: backlinks.filter(b => b.priority === 'high').length,
  };

  return (
    <div className="space-y-6" data-testid="backlinks-tab">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link2 className="h-5 w-5 text-primary" />
          <div>
            <h2 className="text-lg font-bold">{t('Opportunites Backlinks', 'Backlink Opportunities')}</h2>
            <p className="text-sm text-muted-foreground">{t('Sites pertinents pour obtenir des liens entrants', 'Relevant sites to get inbound links')}</p>
          </div>
        </div>
        {isAdmin && (
          <div className="flex flex-col items-end gap-1.5">
            <Button onClick={generate} disabled={generating || isReached} data-testid="generate-backlinks-btn">
              {generating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
              {isReached ? t('Quota atteint', 'Quota reached') : t('Generer', 'Generate')}
            </Button>
            <AiQuotaInline quota={quota} />
          </div>
        )}
      </div>

      {backlinks.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold">{stats.total}</p><p className="text-xs text-muted-foreground">{t('Total', 'Total')}</p></CardContent></Card>
          <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-green-600">{stats.obtained}</p><p className="text-xs text-muted-foreground">{t('Obtenus', 'Obtained')}</p></CardContent></Card>
          <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-slate-600">{stats.pending}</p><p className="text-xs text-muted-foreground">{t('En attente', 'Pending')}</p></CardContent></Card>
          <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-red-600">{stats.high}</p><p className="text-xs text-muted-foreground">{t('Haute priorite', 'High priority')}</p></CardContent></Card>
        </div>
      )}

      {backlinks.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-muted-foreground">
            <Link2 className="h-12 w-12 mb-4 opacity-40" />
            <p className="font-medium">{t('Aucune opportunite de backlink', 'No backlink opportunities')}</p>
            <p className="text-sm">{t('Cliquez "Generer" pour identifier des sites pertinents', 'Click "Generate" to identify relevant sites')}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {backlinks.map(bl => (
            <Card key={bl.backlink_id} className="hover:shadow-md transition-shadow" data-testid={`backlink-${bl.backlink_id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <h3 className="font-semibold text-sm">{bl.site_name}</h3>
                      <Badge className={TYPE_COLORS[bl.type] || TYPE_COLORS.annuaire} variant="secondary">
                        {lang === 'fr' ? TYPE_LABELS[bl.type] : TYPE_LABELS_EN[bl.type] || bl.type}
                      </Badge>
                      <Badge className={PRIORITY_COLORS[bl.priority] || PRIORITY_COLORS.medium} variant="secondary">
                        {bl.priority === 'high' ? t('Haute', 'High') : bl.priority === 'medium' ? t('Moyenne', 'Medium') : t('Basse', 'Low')}
                      </Badge>
                      {bl.estimated_da > 0 && (
                        <span className={`inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full ${bl.estimated_da >= 60 ? 'bg-green-500/10 text-green-700' : bl.estimated_da >= 30 ? 'bg-amber-500/10 text-amber-700' : 'bg-slate-500/10 text-slate-600'}`}>
                          DA {bl.estimated_da}
                        </span>
                      )}
                    </div>
                    <a href={bl.url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline flex items-center gap-1 mb-2">
                      <ExternalLink className="h-3 w-3" />{bl.url}
                    </a>
                    <p className="text-sm text-muted-foreground">{bl.suggested_action}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Select value={bl.status} onValueChange={(val) => updateStatus(bl.backlink_id, val)}>
                      <SelectTrigger className="w-[130px] h-8 text-xs" data-testid={`backlink-status-${bl.backlink_id}`}>
                        <Badge className={STATUS_COLORS[bl.status] || STATUS_COLORS.pending} variant="secondary">
                          {lang === 'fr' ? STATUS_LABELS[bl.status] : STATUS_LABELS_EN[bl.status] || bl.status}
                        </Badge>
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(lang === 'fr' ? STATUS_LABELS : STATUS_LABELS_EN).map(([val, label]) => (
                          <SelectItem key={val} value={val}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {isAdmin && (
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => remove(bl.backlink_id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
